import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/** Configuração do login do Instagram. */
export interface InstagramLoginConfig {
    appId: string;
    escopos: string;
    /** URL pronta do diálogo do Instagram, já com redirect_uri, scope e state. */
    urlAutorizacao: string;
}

/** Endereço para onde a Meta devolve o navegador. Registrado no App Dashboard (Instagram → login do Instagram). */
export const INSTAGRAM_REDIRECT_PATH = '/integracoes/instagram/callback';
export const INSTAGRAM_AUTH_MESSAGE = 'lemeai:instagram-auth';

export const montarInstagramRedirectUri = () =>
    `${window.location.origin}${INSTAGRAM_REDIRECT_PATH}`;

/** Valor de `tipoLogin` que a API devolve. `facebook_login` só aparece em conexões antigas, feitas pela Página. */
export type TipoLoginInstagram = 'facebook_login' | 'instagram_login';

export interface InstagramAccount {
    /** Página do Facebook — só nas conexões antigas, feitas pelo Login do Facebook. */
    paginaId?: string | null;
    paginaNome?: string | null;
    instagramPageId?: string;
    instagramUsername?: string;
    webhooksAtivos: boolean;
    tokenExpiraEm?: string | null;
    tipoLogin?: TipoLoginInstagram;
}

export interface InstagramStatusResponse {
    sucesso: boolean;
    mensagem?: string;
    dados: {
        conectado: boolean;
        contas: InstagramAccount[];
    };
}

type RespostaConectar = { sucesso: boolean; mensagem?: string; dados?: InstagramAccount[] };

const enviarCode = async (rota: string, code: string): Promise<RespostaConectar> => {
    try {
        // Precisa ser idêntico ao redirect_uri do diálogo: a Meta recusa a troca do code sem ele.
        const redirectUri = montarInstagramRedirectUri();

        const response = await apiFetch(`${API_URL}/api/instagram/${rota}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, redirectUri })
        });

        if (response.ok) {
            return await response.json();
        }

        const errorData = await response.json().catch(() => null);
        return { sucesso: false, mensagem: errorData?.mensagem || 'Erro ao conectar Instagram.' };
    } catch (error) {
        console.error('Erro ao conectar Instagram:', error);
        return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
    }
};

export const InstagramService = {
    /**
     * URL do diálogo do login do Instagram (www.instagram.com/oauth/authorize).
     * A API recusa com mensagem clara quando o servidor não tem as credenciais do app do Instagram.
     */
    getConfiguracaoLoginInstagram: async (state: string): Promise<{ sucesso: boolean; mensagem?: string; dados?: InstagramLoginConfig }> => {
        try {
            const query = new URLSearchParams({ redirectUri: montarInstagramRedirectUri(), state });
            const response = await apiFetch(`${API_URL}/api/instagram/ConfiguracaoLoginInstagram?${query}`);
            const corpo = await response.json().catch(() => null);
            if (response.ok && corpo) return corpo;
            return { sucesso: false, mensagem: corpo?.mensagem || 'Login pelo Instagram indisponível.' };
        } catch (error) {
            console.error('Erro ao obter configuração do login do Instagram:', error);
            return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
        }
    },

    /**
     * Envia o authorization code do login do Instagram. O code chega com `#_` no
     * fim e vai cru: quem limpa é o backend.
     */
    conectarComLoginInstagram: (code: string) => enviarCode('conectar-instagram', code),

    /**
     * Recupera o status atual da conexão com o Instagram.
     */
    getStatus: async (): Promise<InstagramStatusResponse> => {
        try {
            const response = await apiFetch(`${API_URL}/api/instagram/status`);
            if (response.ok) {
                return await response.json();
            }
        } catch (error) {
            console.error('Erro ao obter status do Instagram:', error);
        }

        return {
            sucesso: false,
            dados: { conectado: false, contas: [] }
        };
    },

    /**
     * Desconecta uma conta pelo IGID (ou pelo page_id, nas conexões antigas feitas pela Página).
     */
    desconectar: async (identificador: string): Promise<{ sucesso: boolean; mensagem?: string }> => {
        try {
            const response = await apiFetch(`${API_URL}/api/instagram/desconectar/${encodeURIComponent(identificador)}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                return await response.json();
            }

            const errorData = await response.json().catch(() => null);
            return { sucesso: false, mensagem: errorData?.mensagem || 'Erro ao desconectar.' };
        } catch (error) {
            console.error('Erro ao desconectar Instagram:', error);
            return { sucesso: false, mensagem: 'Erro de conexão com o servidor.' };
        }
    }
};
